import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Camera, Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";

/**
 * PropertyPhotos
 * Compact photo gallery + uploader for a property record.
 *
 * Props:
 *   propertyId  — id of the property (required to persist)
 *   photos      — string[] of base64 data URLs
 *   onUpdated   — called after add/delete so parent can re-fetch
 */
export default function PropertyPhotos({ propertyId, photos = [], onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // { src, idx }
  const inputRef = useRef(null);

  const readAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });

  const handleFiles = async (files) => {
    if (!propertyId) return toast.error("Save the property first before adding photos");
    const arr = Array.from(files || []);
    if (!arr.length) return;
    // Filter to images + size guard (5 MB per file → prevents 16MB Mongo doc limit)
    const valid = arr.filter((f) => {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name} isn't an image — skipped`);
        return false;
      }
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`${f.name} is over 5MB — skipped`);
        return false;
      }
      return true;
    });
    if (!valid.length) return;
    setUploading(true);
    try {
      const base64s = await Promise.all(valid.map(readAsDataUrl));
      const { data } = await api.post(`/properties/${propertyId}/photos`, {
        photos: base64s,
      });
      toast.success(`Added ${data.added} photo${data.added === 1 ? "" : "s"}`);
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removePhoto = async (idx) => {
    if (!propertyId) return;
    if (!window.confirm("Remove this photo?")) return;
    try {
      await api.delete(`/properties/${propertyId}/photos/${idx}`);
      toast.success("Photo removed");
      setPreview(null);
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="property-photos">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
          <Camera size={11} /> Property Photos
          {photos.length > 0 && <span className="text-foreground">· {photos.length}</span>}
        </div>
        <label className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-slate-900 text-white cursor-pointer hover:bg-slate-800 transition-colors"
               data-testid="property-photos-upload-btn">
          {uploading
            ? <><Loader2 size={11} className="animate-spin" /> Uploading…</>
            : <><Upload size={11} /> Add photos</>}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            disabled={uploading}
            data-testid="property-photos-file-input"
          />
        </label>
      </div>

      {photos.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-[var(--app-line)] bg-[var(--app-surface)]/40 p-6 text-center text-xs text-muted-foreground">
          <ImageIcon size={20} className="mx-auto mb-2 opacity-40" />
          No photos yet — upload interior/exterior shots to share with buyers,
          contractors, or the title company.
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
          {photos.map((p, i) => (
            <button
              key={i}
              onClick={() => setPreview({ src: p, idx: i })}
              className="aspect-square rounded-lg overflow-hidden border border-[var(--app-line)] hover:ring-2 ring-slate-900 transition-all"
              data-testid={`property-photo-${i}`}
            >
              <img src={p} alt={`photo-${i + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid="property-photo-lightbox"
        >
          <div className="relative max-w-4xl max-h-[90vh] flex flex-col items-center gap-3"
               onClick={(e) => e.stopPropagation()}>
            <img src={preview.src} alt="preview"
                 className="max-w-full max-h-[80vh] rounded-xl object-contain" />
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                onClick={() => setPreview(null)}
                data-testid="property-photo-close-btn"
              >
                Close
              </Button>
              <Button
                variant="destructive"
                onClick={() => removePhoto(preview.idx)}
                data-testid="property-photo-delete-btn"
              >
                <X size={13} className="mr-1" /> Remove
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
